import type { FieldErrors, UseFormSetValue } from 'react-hook-form';
import { Card } from '@/components/ui/Card';
import { AvatarUploadField } from '@/components/common/AvatarUploadField';
import { CoverPhotoUploadField } from '@/components/common/CoverPhotoUploadField';
import { PARTNER_LISTING_BAND_ASPECT_CLASS } from '@/lib/constants/image-crop';
import { initialsFromName } from '@/lib/utils/user.utils';
import type { PartnerProfileData } from '../../api/partner-profile.api';
import type { PartnerProfileFormValues } from './schema';
import { PARTNER_PROFILE_STRINGS as S } from './partner-profile.strings';

interface PartnerImagesSectionProps {
  data: PartnerProfileData;
  setValue: UseFormSetValue<PartnerProfileFormValues>;
  errors?: FieldErrors<PartnerProfileFormValues>;
  trackUpload: (id: string) => (isUploading: boolean) => void;
}

// The preview takes the crop frame's own shape so it shows the whole saved image.
// The live detail band (256 px tall, full page width) is far wider than this panel,
// so a fixed height here would show only a thin slice of the banner.
const COVER_PREVIEW_CLASS = 'aspect-[1600/460] w-full';
const LISTING_PREVIEW_CLASS = `${PARTNER_LISTING_BAND_ASPECT_CLASS} w-full`;
const BLOCK_CLASS = 'flex flex-col gap-2 border-t border-divider pt-6';

function ImagesEditor({ data, setValue, errors, trackUpload }: PartnerImagesSectionProps) {
  const dirty = { shouldDirty: true, shouldValidate: true } as const;
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <span className="text-text-muted text-[12px] font-semibold tracking-[0.08em] uppercase">
          {S.LOGO_LABEL}
        </span>
        <AvatarUploadField
          inline
          photoUrl={data.logoUrl ?? undefined}
          initials={initialsFromName(data.businessName ?? '')}
          uploadKind="partnerLogo"
          accept="image/png,image/jpeg"
          uploadCaption={S.LOGO_UPLOAD_CAPTION}
          hint={S.LOGO_HINT}
          tooLargeMessage={S.LOGO_TOO_LARGE}
          errorMessage={S.LOGO_UPLOAD_ERROR}
          error={errors?.logoFileKey?.message}
          onUploaded={(result) => setValue('logoFileKey', result.fileKey, dirty)}
          onUploadingChange={trackUpload('logo')}
        />
      </div>
      <div className={BLOCK_CLASS}>
        <CoverPhotoUploadField
          coverPhotoUrl={data.coverPhotoUrl ?? undefined}
          onUploaded={(fileKey) => setValue('coverPhotoFileKey', fileKey, dirty)}
          onUploadingChange={trackUpload('cover')}
          uploadKind="partnerCoverPhoto"
          label={S.COVER_PHOTO_LABEL}
          hint={S.COVER_PHOTO_HINT}
          uploadCaption={S.COVER_PHOTO_UPLOAD}
          replaceCaption={S.COVER_PHOTO_REPLACE}
          wrongTypeMessage={S.LOGO_WRONG_TYPE}
          tooLargeMessage={S.COVER_PHOTO_TOO_LARGE}
          uploadFailedMessage={S.COVER_PHOTO_UPLOAD_ERROR}
          error={errors?.coverPhotoFileKey?.message}
          stacked
          previewClassName={COVER_PREVIEW_CLASS}
        />
      </div>
      <div className={BLOCK_CLASS}>
        <CoverPhotoUploadField
          coverPhotoUrl={data.listingCoverPhotoUrl ?? undefined}
          onUploaded={(fileKey) => setValue('listingCoverFileKey', fileKey, { shouldDirty: true })}
          onUploadingChange={trackUpload('listingCover')}
          uploadKind="partnerListingCover"
          label={S.LISTING_COVER_LABEL}
          hint={S.LISTING_COVER_HINT}
          uploadCaption={S.LISTING_COVER_UPLOAD}
          replaceCaption={S.LISTING_COVER_REPLACE}
          wrongTypeMessage={S.LOGO_WRONG_TYPE}
          tooLargeMessage={S.COVER_PHOTO_TOO_LARGE}
          uploadFailedMessage={S.COVER_PHOTO_UPLOAD_ERROR}
          stacked
          previewClassName={LISTING_PREVIEW_CLASS}
        />
      </div>
    </div>
  );
}

/**
 * The logo and cover images panel (FR-177), opened from the header's Edit:
 * the logo, detail cover and optional listing cover. The logo and detail
 * cover are required (FR-116); the schema's refinement reports a missing one
 * here.
 */
export function PartnerImagesSection(props: PartnerImagesSectionProps) {
  return (
    <Card title={S.IMAGES} headerDivider hoverEffect={false}>
      <ImagesEditor {...props} />
    </Card>
  );
}
